import { Button, EmptyState, Spinner, Toaster, TooltipProvider } from "@auradata/ui";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { ApiError } from "@/lib/api/client";
import { applyPalette, applyTheme, useUiStore, watchSystemTheme } from "@/lib/stores/ui";

export interface RouterContext {
  queryClient: QueryClient;
}

function RootLayout() {
  const theme = useUiStore((s) => s.theme);
  const palette = useUiStore((s) => s.palette);
  useEffect(() => {
    applyTheme(theme);
    return watchSystemTheme(theme);
  }, [theme]);
  useEffect(() => applyPalette(palette), [palette]);
  return (
    <TooltipProvider delayDuration={300}>
      <Outlet />
      <Toaster />
    </TooltipProvider>
  );
}

/** In-shell loading state: keeps sidebar and top bar while a page chunk or its data loads. */
export function PagePending() {
  return (
    <div className="flex min-h-64 flex-1 items-center justify-center text-fg-subtle">
      <Spinner size={16} />
    </div>
  );
}

export function PendingPage() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas text-fg-subtle">
      <Spinner size={16} />
    </div>
  );
}

export function ErrorPage({ error, reset }: { error: Error; reset: () => void }) {
  const status = error instanceof ApiError ? error.status : undefined;
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas">
      <EmptyState
        title={status === 404 ? "Not found" : "Something went wrong"}
        description={error.message}
        action={
          <Button variant="primary" onClick={reset}>
            Try again
          </Button>
        }
      />
    </div>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  pendingComponent: PendingPage,
  errorComponent: ErrorPage,
});
